import { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Edit3, Check } from 'lucide-react';

interface Strategy {
  id: string;
  name: string;
  type: string;
  status: 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'ARCHIVED';
  brandLogo?: string;
  brandName?: string;
  brandColor?: string;
  description: string;
  categories: string[];
  progress: number;
  checklist: {
    label: string;
    value: string;
    status: 'done' | 'pending' | 'in-progress';
  }[];
}

const mockStrategies: Strategy[] = [
  {
    id: '1',
    name: 'Gardena PL - Fall 2025',
    type: 'Product',
    status: 'DRAFT',
    brandName: 'GARDENA',
    brandColor: '#FF6B35',
    description: 'A garden tool designed to collect leaves and lawn debris, part of Gardena modular system. The leaf collector helps you sweep up fallen leaves quickly and efficiently, keeping lawns tidy and collecting debris for composting. It features a wide intake, a bag for easy disposal, and ergonomic handling to reduce fatigue during longer use. Compatible with standard Gardena handles and other system accessories to streamline yard cleanup.',
    categories: ['GARDEN TOOL', 'LEAF COLLECTION', 'LAWN CARE', 'GARDENA SYSTEM', 'ERGONOMIC DESIGN'],
    progress: 100,
    checklist: [
      { label: 'Settings', value: 'PL', status: 'done' },
      { label: 'Objectives', value: 'AWARENESS', status: 'done' },
      { label: 'Audit', value: 'Done', status: 'done' },
      { label: 'Audience', value: 'Full Audience', status: 'done' },
      { label: 'Concept', value: 'Nie dostaniasz z śćcia', status: 'done' },
      { label: 'Mediaplan', value: 'Done', status: 'done' }
    ]
  },
  {
    id: '2',
    name: 'test',
    type: 'Product',
    status: 'DRAFT',
    brandName: 'pet.fL',
    brandColor: '#FF9500',
    description: 'Launch Adult All Breed Grain Free is a pet food product designed for adult dogs of any breed. It is grain-free, catering to pets with specific dietary requirements or preferences.',
    categories: ['PET FOOD', 'GRAIN-FREE', 'ADULT DOGS', 'ALL BREED', 'PREMIUM NUTRITION'],
    progress: 100,
    checklist: [
      { label: 'Settings', value: 'SE', status: 'done' },
      { label: 'ICPs', value: 'Done', status: 'done' },
      { label: 'Moodboard', value: 'Done', status: 'done' },
      { label: 'Creators', value: 'Done', status: 'done' }
    ]
  }
];

export function StrategiesPage() {
  const [strategies] = useState<Strategy[]>(mockStrategies);

  const getStatusColor = (status: Strategy['status']) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-orange-50 text-orange-600 border-orange-200';
      case 'ACTIVE':
        return 'bg-green-50 text-green-600 border-green-200';
      case 'COMPLETED':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'ARCHIVED':
        return 'bg-gray-50 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] p-2.5">
      <div className="min-h-[calc(100vh-20px)] rounded-2xl bg-white border border-gray-200/80 shadow-sm overflow-hidden">
      <div className="max-w-5xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-gray-900">Campaign Plans</h1>
        </div>

        {/* Strategy List */}
        <div className="space-y-6">
          {strategies.map((strategy, _index) => (
            <Card key={strategy.id} className="border-gray-200 bg-white shadow-sm">
              <CardContent className="p-8">
                {/* Header Row */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl text-gray-900 mb-1">{strategy.name}</h2>
                    <p className="text-sm text-gray-500">Type: {strategy.type}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-gray-500 uppercase mb-1">Status</p>
                      <Badge className={`${getStatusColor(strategy.status)} border px-3 py-1`}>
                        {strategy.status}
                      </Badge>
                    </div>
                    <Button variant="outline" className="gap-2 border-gray-300">
                      <Edit3 className="w-4 h-4" />
                      Edit Plan
                    </Button>
                  </div>
                </div>

                {/* Main Content */}
                <div className="flex gap-8">
                  {/* Left Side - Brand Logo, Description, Tags */}
                  <div className="flex-1">
                    {/* Brand Logo */}
                    {strategy.brandName && (
                      <div className="mb-6">
                        <div 
                          className="inline-flex items-center justify-center px-4 py-2 rounded"
                          style={{ backgroundColor: strategy.brandColor }}
                        >
                          <span className="text-white text-sm uppercase tracking-wide">
                            {strategy.brandName}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    <p className="text-sm text-gray-700 leading-relaxed mb-6">
                      {strategy.description}
                    </p>

                    {/* Category Tags */}
                    <div className="flex flex-wrap gap-2">
                      {strategy.categories.map((category, idx) => (
                        <div 
                          key={idx}
                          className="px-3 py-1 bg-white border border-gray-300 rounded text-xs text-gray-700 uppercase tracking-wide"
                        >
                          {category}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Side - Checklist */}
                  <div className="w-72 flex-shrink-0">
                    <div className="space-y-3">
                      {strategy.checklist.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          {/* Number/Check Circle */}
                          <div className="flex-shrink-0 mt-0.5">
                            {item.status === 'done' ? (
                              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            ) : item.status === 'in-progress' ? (
                              <div className="w-6 h-6 rounded-full border-2 border-gray-400 flex items-center justify-center">
                                <span className="text-xs text-gray-600">{idx + 1}</span>
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center">
                                <span className="text-xs text-gray-400">{idx + 1}</span>
                              </div>
                            )}
                          </div>

                          {/* Label and Value */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-4">
                              <span className="text-sm text-gray-600">{item.label}</span>
                              <span className="text-sm text-gray-900 font-medium">
                                {item.value}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-600">Progress: {strategy.progress}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${strategy.progress}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}